import { FC, useState } from "react";

import { SilentVoteModelDocument } from "./Vote";

import classes from "./Vote.module.css";
import SectionHeader from "../Layout/SectionHeader";
import BottomSticky from "../UI/BottomSticky";
import Button from "../UI/Button";
import { getLang, i18n } from "../../i18n";

interface Props extends SilentVoteModelDocument {
  voteTipViewed: boolean;
  setVoteTipViewed: React.Dispatch<React.SetStateAction<boolean>>;
  silentVoteHandler: (
    _id: string,
    color: string,
    deck: boolean
  ) => Promise<void>;
  skipVoteHandler: (_id: string) => Promise<void>;
  reportHandler: (_id: string) => Promise<void>;
  balance: number;
}

const SilentVote: FC<Props> = ({
  _id,
  voteTipViewed,
  setVoteTipViewed,
  artwork1ID,
  artwork1Src,
  artwork2ID,
  artwork2Src,
  silentVoteHandler,
  skipVoteHandler,
  reportHandler,
  balance,
  question,
}) => {
  const lang = getLang();
  const t = i18n.silentVote[lang];

  const [animate, setAnimate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [tipStep, setTipStep] = useState(0);
  const [deckArtwork, setDeckArtwork] = useState("");
  const voteClickHandler = () => {
    setIsLoading(true);
    setAnimate(true);
    setTimeout(() => {
      silentVoteHandler(_id, selected, deckArtwork === selected ? true : false);
      setIsLoading(false);
    }, 1500);
  };
  const setArtworkHandler = (artworkID: string) => {
    if (deckArtwork === artworkID) {
      setDeckArtwork("");
      return;
    }
    setDeckArtwork(artworkID);
  };
  const transform = 30 * Math.floor(Math.random() * (5 - 1 + 1) + 1) - 120;
  return (
    <>
      {!voteTipViewed && (
        <div className={classes.tip}>
          <div className={classes.tipBg}></div>
          <div className={classes.tipPopup}>
            <div className={classes.tipPopupBody}>
              <div
                className={classes.tipPopupContent}
                style={{ transform: `translate3d(${-100 * tipStep}%, 0, 0)` }}
              >
                <div
                  className={`${classes.tipStep} ${
                    tipStep === 0 ? classes.active : ""
                  }`}
                >
                  <h2>{t.tip.step1Title}</h2>
                  <p>{t.tip.step1Text}</p>
                  <Button
                    secondary
                    label={t.tip.nextBtn}
                    onClick={() => setTipStep(1)}
                  />
                </div>
                <div
                  className={`${classes.tipStep} ${
                    tipStep === 1 ? classes.active : ""
                  }`}
                >
                  <h2>{t.tip.step2Title}</h2>
                  <p>{t.tip.step2Text}</p>
                  <Button
                    secondary
                    label={t.tip.nextBtn}
                    onClick={() => setTipStep(2)}
                  />
                </div>
                <div
                  className={`${classes.tipStep} ${
                    tipStep === 2 ? classes.active : ""
                  }`}
                >
                  <h2>{t.tip.step3Title}</h2>
                  <p>{t.tip.step3Text}</p>
                  <Button
                    label={t.tip.step3Btn}
                    onClick={() => setVoteTipViewed(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <SectionHeader
        label={question || t.selectArtwork}
        back="/votes"
        skip={_id}
        skipHandler={skipVoteHandler}
        loader={true}
        black={true}
      />
      <section className={classes.voting}>
        <div className={classes.photos}>
          <div
            className={`${classes.photo} ${
              selected === artwork1ID ? classes.selected : ""
            }`}
            onClick={() =>
              setSelected((prevState) =>
                prevState === artwork1ID ? "" : artwork1ID
              )
            }
          >
            <img src={artwork1Src} alt="" />
            {selected === artwork1ID && (
              <div
                className={`${classes.deck} ${
                  deckArtwork === artwork1ID ? classes.selected : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation(); // prevent event from bubbling
                  setArtworkHandler(artwork1ID);
                }}
              >
                ⭐
              </div>
            )}
            {selected === artwork1ID && (
              <div
                className={classes.report}
                onClick={() => {
                  reportHandler(artwork1ID);
                }}
              >
                ⚠️
              </div>
            )}
            {selected === artwork1ID && (
              <div className={classes.checkbox}>
                <svg
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="8.5" cy="8.5" r="8.5" fill="white" />
                  <circle cx="8.5" cy="8.5" r="6.5" fill="#4ECC5E" />
                  <path
                    d="M12 6L6.44898 11L4.49931 9.05102"
                    stroke="white"
                    strokeWidth="1.58333"
                  />
                </svg>
              </div>
            )}
          </div>
          <div
            className={`${classes.photo} ${
              selected === artwork2ID ? classes.selected : ""
            }`}
            onClick={() =>
              setSelected((prevState) =>
                prevState === artwork2ID ? "" : artwork2ID
              )
            }
          >
            <img src={artwork2Src} alt="" />
            {selected === artwork2ID && (
              <div
                className={`${classes.deck} ${
                  deckArtwork === artwork2ID ? classes.selected : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation(); // prevent event from bubbling
                  setArtworkHandler(artwork2ID);
                }}
              >
                ⭐
              </div>
            )}
            {selected === artwork2ID && (
              <div
                className={classes.report}
                onClick={() => {
                  reportHandler(artwork2ID);
                }}
              >
                ⚠️
              </div>
            )}
            {selected === artwork2ID && (
              <div className={classes.checkbox}>
                <svg
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="8.5" cy="8.5" r="8.5" fill="white" />
                  <circle cx="8.5" cy="8.5" r="6.5" fill="#4ECC5E" />
                  <path
                    d="M12 6L6.44898 11L4.49931 9.05102"
                    stroke="white"
                    strokeWidth="1.58333"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
      </section>
      <div className={classes.balance}>
        <span>{t.balance}: </span> {balance.toFixed(2)} 🌕
      </div>
      <div
        className={`${classes.sticky} ${
          selected !== "" ? classes.visible : ""
        }`}
      >
        <BottomSticky>
          <Button
            label={deckArtwork ? t.voteBtn.withDeck : t.voteBtn.default}
            disabled={isLoading}
            onClick={() => voteClickHandler()}
          />
        </BottomSticky>
      </div>
      <div
        style={{ transform: `translate3d(${transform}%,0,0)` }}
        className={`${classes.voteEmoji} ${animate ? classes.animated : ""}`}
      >
        +0.01 🌕
      </div>
    </>
  );
};

export default SilentVote;
